import React, { useState } from 'react'
import styled from '@emotion/styled'
import { useMutation } from '../../../../api/graphql'
import gql from 'graphql-tag'
import { Image as CloudinaryImage } from 'cloudinary-react'
import toastr from 'toastr'
import { CurrentUserQuery } from '../../../../providers/CurrentUserContext'
import { IconGlyph } from '../../../../icons/IconGlyph'

const ACCEPT_MUTATION = gql`
  mutation OrganizationMembershipAcceptById($id: ID!) {
    organizationMembershipAcceptById(input: { id: $id }) {
      membership {
        id
      }
      errors
    }
  }
`

const DECLINE_MUTATION = gql`
  mutation OrganizationMembershipDecline($id: ID!) {
    organizationMembershipDecline(input: { id: $id }) {
      success
      errors
    }
  }
`

type Props = {
  invitation: {
    id: string
    invitedAt: string
    expiresAt: string
    organization: {
      id: string
      name: string
      logoPublicId: string | null
      memberCount: number
    }
  }
  onAccept: (org: { id: string; name: string }) => void
  onDecline: () => void
}

const PendingInvitationCard: React.FC<Props> = ({ invitation, onAccept, onDecline }) => {
  const [accepting, setAccepting] = useState(false)
  const [declining, setDeclining] = useState(false)

  const [acceptMutation] = useMutation(ACCEPT_MUTATION, {
    refetchQueries: [{ query: CurrentUserQuery }],
  })
  const [declineMutation] = useMutation(DECLINE_MUTATION, {
    refetchQueries: [{ query: CurrentUserQuery }],
  })

  const handleAccept = async () => {
    setAccepting(true)
    try {
      const result = await acceptMutation({ variables: { id: invitation.id } })
      if (result.data?.organizationMembershipAcceptById?.errors?.length > 0) {
        toastr.error(result.data.organizationMembershipAcceptById.errors.join(', '))
      } else {
        toastr.success(`You've joined ${invitation.organization.name}!`)
        onAccept({ id: invitation.organization.id, name: invitation.organization.name })
      }
    } catch (error) {
      toastr.error('Failed to accept invitation')
    } finally {
      setAccepting(false)
    }
  }

  const handleDecline = async () => {
    setDeclining(true)
    try {
      const result = await declineMutation({ variables: { id: invitation.id } })
      if (result.data?.organizationMembershipDecline?.errors?.length > 0) {
        toastr.error(result.data.organizationMembershipDecline.errors.join(', '))
      } else {
        toastr.success('Invitation declined')
        onDecline()
      }
    } catch (error) {
      toastr.error('Failed to decline invitation')
    } finally {
      setDeclining(false)
    }
  }

  const expiresAt = new Date(invitation.expiresAt)
  const daysRemaining = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24))

  return (
    <Card>
      <OrgInfo>
        {invitation.organization.logoPublicId ? (
          <CloudinaryImage cloudName={process.env.CLOUDINARY_CLOUD_NAME} publicId={invitation.organization.logoPublicId} width="48" height="48" crop="fill" />
        ) : (
          <DefaultLogo>
            <IconGlyph name="building" />
          </DefaultLogo>
        )}
        <OrgDetails>
          <OrgName>{invitation.organization.name}</OrgName>
          <MemberCount>{invitation.organization.memberCount} member(s)</MemberCount>
          {daysRemaining > 0 && (
            <ExpiresText>
              Expires in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
            </ExpiresText>
          )}
        </OrgDetails>
      </OrgInfo>
      <Actions>
        <AcceptButton onClick={handleAccept} disabled={accepting || declining}>
          {accepting ? 'Accepting...' : 'Accept'}
        </AcceptButton>
        <DeclineButton onClick={handleDecline} disabled={accepting || declining}>
          {declining ? 'Declining...' : 'Decline'}
        </DeclineButton>
      </Actions>
    </Card>
  )
}

const Card = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: white;
  border: 1px solid #f3f4f6;
  border-radius: 8px;
  gap: 16px;
`

const OrgInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`

const DefaultLogo = styled.div`
  width: 48px;
  height: 48px;
  background: #e5e7eb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #9ca3af;
`

const OrgDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const OrgName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #111827;
`

const MemberCount = styled.div`
  font-size: 12px;
  color: #6b7280;
`

const ExpiresText = styled.div`
  font-size: 11px;
  color: #db2777;
  font-weight: 500;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
`

const AcceptButton = styled.button`
  padding: 8px 16px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #059669;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const DeclineButton = styled.button`
  padding: 8px 16px;
  background: #f3f4f6;
  color: #ef4444;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #e5e7eb;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export default PendingInvitationCard
